import React, { useContext, useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Box, IconButton } from "@material-ui/core";
import SendIcon from "@material-ui/icons/Send";
import clsx from "clsx";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { emitTicketAnnotationCreated } from "../../helpers/ticketAnnotationEvents";
import { i18n } from "../../translate/i18n";
import { ThemeContext } from "../../context/Theme/ThemeContext";

const useStyles = makeStyles(() => ({
	legacyPanel: {
		width: "100%",
		boxSizing: "border-box",
		border: "1px solid #f59e0b",
		backgroundColor: "#fffbeb",
		borderRadius: 8,
		padding: 12,
		color: "#92400e",
		maxHeight: "min(38vh, 260px)",
		overflowY: "auto",
	},
	legacyPanelDark: {
		borderColor: "#b45309",
		backgroundColor: "#422006",
		color: "#fde68a",
	},
	legacyTitle: {
		display: "block",
		fontSize: "0.875rem",
		fontWeight: 600,
		marginBottom: 4,
	},
	legacyText: {
		margin: "0 0 8px",
		fontSize: "0.8125rem",
		lineHeight: 1.45,
	},
	legacyHint: {
		margin: "0 0 12px",
		fontSize: "0.8125rem",
		lineHeight: 1.45,
		opacity: 0.92,
	},
	legacyComposer: {
		display: "flex",
		alignItems: "flex-end",
		gap: 8,
		width: "100%",
	},
	legacyTextarea: {
		flex: "1 1 auto",
		minWidth: 0,
		width: "100%",
		minHeight: 56,
		maxHeight: 96,
		resize: "vertical",
		border: "1px solid #f59e0b",
		borderRadius: 8,
		padding: "8px 12px",
		fontSize: "0.875rem",
		lineHeight: 1.45,
		fontFamily: "Roboto, Helvetica, Arial, sans-serif",
		backgroundColor: "rgba(255, 255, 255, 0.85)",
		color: "#92400e",
		boxSizing: "border-box",
		"&:focus": {
			outline: "none",
			borderColor: "#d97706",
			boxShadow: "0 0 0 2px rgba(245, 158, 11, 0.25)",
		},
		"&::placeholder": {
			color: "#b45309",
			opacity: 0.75,
		},
	},
	legacyTextareaDark: {
		backgroundColor: "rgba(66, 32, 6, 0.55)",
		color: "#fde68a",
		borderColor: "#b45309",
		"&:focus": {
			borderColor: "#f59e0b",
		},
		"&::placeholder": {
			color: "#fcd34d",
			opacity: 0.75,
		},
	},
	legacySendBtn: {
		flexShrink: 0,
		marginBottom: 2,
	},
	sendMessageIcons: {
		cursor: "pointer",
		color: "#92400e",
	},
	sendMessageIconsDark: {
		color: "#fde68a",
	},
}));

const buildAnnotationBody = (text) =>
	`<p>${text
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/\n/g, "<br>")}</p>`;

function DraftInput({ ticketId, tailwickLayout = false }) {
	const [annotationText, setAnnotationText] = useState("");
	const { darkMode } = useContext(ThemeContext);
	const classes = useStyles();

	const handleSendAnnotation = () => {
		const text = annotationText.trim();
		if (!text) return;

		(async () => {
			try {
				const { data } = await api.post(`/messages/annotation/${ticketId}`, {
					body: buildAnnotationBody(text),
				});

				setAnnotationText("");
				if (data?.id) {
					emitTicketAnnotationCreated(ticketId, data);
				}
			} catch (err) {
				toastError(err);
			}
		})();
	};

	useEffect(() => {
		setAnnotationText("");
	}, [ticketId]);

	const sendDisabled = !annotationText.trim();

	if (tailwickLayout) {
		return (
			<div
				className="tailwick-private-message-panel"
				role="region"
				aria-label={i18n.t("messagesInput.privateMessageAlertTitle")}
			>
				<strong>{i18n.t("messagesInput.privateMessageAlertTitle")}</strong>
				<p className="tailwick-private-message-panel__text">
					{i18n.t("messagesInput.privateMessageAlert")}
				</p>
				<p className="tailwick-private-message-panel__hint">
					{i18n.t("messagesInput.privateMessageAlertHint")}
				</p>
				<div className="tailwick-private-message-panel__composer tailwick-composer-row">
					<div className="tailwick-composer-input-wrap tailwick-private-annotation-wrap">
						<textarea
							className="tailwick-private-annotation-input"
							value={annotationText}
							onChange={(event) => setAnnotationText(event.target.value)}
							onKeyDown={(event) => {
								if (event.key === "Enter" && !event.shiftKey) {
									event.preventDefault();
									handleSendAnnotation();
								}
							}}
							placeholder={i18n.t("messagesInput.privateMessagePlaceholder")}
							rows={1}
						/>
						<div className="tailwick-composer-input-end">
							<button
								type="button"
								aria-label="send-annotation"
								className={clsx(
									"tailwick-composer-infield-btn tailwick-composer-infield-btn--accent",
									sendDisabled && "opacity-50"
								)}
								disabled={sendDisabled}
								onClick={handleSendAnnotation}
							>
								<svg
									xmlns="http://www.w3.org/2000/svg"
									width="20"
									height="20"
									viewBox="0 0 24 24"
									fill="currentColor"
									aria-hidden
								>
									<path d="M2.01 21 23 12 2.01 3 2 10l15 2-15 2z" />
								</svg>
							</button>
						</div>
					</div>
				</div>
			</div>
		);
	}

	return (
		<Box
			className={clsx(classes.legacyPanel, darkMode && classes.legacyPanelDark)}
			role="region"
			aria-label={i18n.t("messagesInput.privateMessageAlertTitle")}
		>
			<strong className={classes.legacyTitle}>
				{i18n.t("messagesInput.privateMessageAlertTitle")}
			</strong>
			<p className={classes.legacyText}>{i18n.t("messagesInput.privateMessageAlert")}</p>
			<p className={classes.legacyHint}>{i18n.t("messagesInput.privateMessageAlertHint")}</p>
			<div className={classes.legacyComposer}>
				<textarea
					className={clsx(
						classes.legacyTextarea,
						darkMode && classes.legacyTextareaDark
					)}
					placeholder={i18n.t("messagesInput.privateMessagePlaceholder")}
					value={annotationText}
					rows={3}
					onChange={(event) => setAnnotationText(event.target.value)}
					onKeyDown={(event) => {
						if (event.key === "Enter" && !event.shiftKey) {
							event.preventDefault();
							handleSendAnnotation();
						}
					}}
				/>
				<IconButton
					aria-label="send-annotation"
					onClick={handleSendAnnotation}
					disabled={sendDisabled}
					className={classes.legacySendBtn}
				>
					<SendIcon
						className={clsx(
							classes.sendMessageIcons,
							darkMode && classes.sendMessageIconsDark
						)}
					/>
				</IconButton>
			</div>
		</Box>
	);
}

DraftInput.displayName = "DraftInput";

export default DraftInput;
